<template>
    <!-- Chip editabile "al volo": click → menu con select/autocomplete → salva -->
    <v-menu
        v-if="editable"
        v-model="menu"
        :close-on-content-click="false"
        offset-y
        bottom
        left
        min-width="280"
    >
        <template v-slot:activator="{ on, attrs }">
            <v-chip
                v-on="on"
                v-bind="attrs"
                :color="color"
                :dark="dark"
                :outlined="outlined"
                :label="square"
                class="ticket-inline-edit__chip"
                :class="[chipClass, { 'ticket-inline-edit__chip--empty': isEmpty }]"
                style="cursor: pointer"
            >
                <v-icon v-if="icon" left small>{{ icon }}</v-icon>
                {{ displayText }}
                <v-icon right x-small>mdi-pencil</v-icon>
            </v-chip>
        </template>

        <v-card class="round-card">
            <v-card-text class="pa-3">
                <div v-if="fieldLabel" class="text-caption grey--text mb-2">{{ fieldLabel }}</div>
                <v-select
                    v-if="!multiple"
                    v-model="draft"
                    :items="options"
                    item-text="text"
                    item-value="value"
                    dense
                    outlined
                    hide-details
                    :menu-props="{ offsetY: true }"
                ></v-select>
                <v-autocomplete
                    v-else
                    v-model="draft"
                    :items="options"
                    item-text="text"
                    item-value="value"
                    multiple
                    chips
                    small-chips
                    deletable-chips
                    dense
                    outlined
                    hide-details
                    :menu-props="{ offsetY: true }"
                ></v-autocomplete>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions class="pa-2">
                <v-spacer></v-spacer>
                <v-btn text small @click="close" :disabled="saving">Annulla</v-btn>
                <v-btn color="primary" small :loading="saving" @click="confirm">
                    <v-icon left small>mdi-content-save</v-icon>
                    Salva
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-menu>

    <!-- Sola lettura: chip statica, stesso aspetto di prima -->
    <v-chip
        v-else
        :color="color"
        :dark="dark"
        :outlined="outlined"
        :label="square"
        class="ticket-inline-edit__chip"
        :class="[chipClass, { 'ticket-inline-edit__chip--empty': isEmpty }]"
    >
        <v-icon v-if="icon" left small>{{ icon }}</v-icon>
        {{ displayText }}
    </v-chip>
</template>

<script>
export default {
    name: 'TicketInlineEdit',

    props: {
        // Id del ticket helpdesk su cui salvare
        ticketId: {
            type: Number,
            required: true
        },
        // Campo da aggiornare: 'status' | 'priority' | 'request_type' | 'users'
        field: {
            type: String,
            required: true
        },
        // Valore corrente: stringa/numero per select singola, array di id per multipla
        value: {
            type: [String, Number, Array],
            default: null
        },
        // Opzioni: [{ value, text }]
        options: {
            type: Array,
            default: () => []
        },
        multiple: {
            type: Boolean,
            default: false
        },
        // Se false la chip è di sola lettura (nessun menu)
        editable: {
            type: Boolean,
            default: false
        },
        color: {
            type: String,
            default: ''
        },
        dark: {
            type: Boolean,
            default: false
        },
        outlined: {
            type: Boolean,
            default: false
        },
        // v-chip "label" (chip squadrata) — rinominato per non confonderlo con fieldLabel
        square: {
            type: Boolean,
            default: false
        },
        icon: {
            type: String,
            default: ''
        },
        emptyText: {
            type: String,
            default: '—'
        },
        // Etichetta mostrata nel menu di modifica
        fieldLabel: {
            type: String,
            default: ''
        },
        // Classi di spaziatura da applicare alla chip (es. 'mr-2'). Non usare
        // `class` sul componente: in modalità editabile finirebbe sul placeholder
        // invisibile del v-menu (la chip viene spostata come sibling dal mixin
        // detachable di Vuetify) e la spaziatura andrebbe persa.
        chipClass: {
            type: String,
            default: ''
        }
    },

    data() {
        return {
            menu: false,
            saving: false,
            draft: this.multiple ? [] : null
        }
    },

    computed: {
        isEmpty() {
            if (this.multiple) {
                return !Array.isArray(this.value) || this.value.length === 0
            }
            return this.value === null || this.value === undefined || this.value === ''
        },

        displayText() {
            if (this.multiple) {
                if (this.isEmpty) {
                    return this.emptyText
                }
                const texts = this.value
                    .map(val => {
                        const opt = this.options.find(o => o.value === val)
                        return opt ? opt.text : null
                    })
                    .filter(Boolean)
                return texts.length ? texts.join(', ') : this.emptyText
            }

            if (this.isEmpty) {
                return this.emptyText
            }
            const opt = this.options.find(o => o.value === this.value)
            return opt ? opt.text : this.emptyText
        },

        hasChanges() {
            if (this.multiple) {
                const current = Array.isArray(this.value) ? [...this.value].sort() : []
                const next = Array.isArray(this.draft) ? [...this.draft].sort() : []
                return JSON.stringify(current) !== JSON.stringify(next)
            }
            return this.draft !== this.value
        }
    },

    watch: {
        menu(open) {
            if (open) {
                this.initDraft()
            }
        }
    },

    methods: {
        initDraft() {
            if (this.multiple) {
                this.draft = Array.isArray(this.value) ? [...this.value] : []
            } else {
                this.draft = this.value
            }
        },

        close() {
            this.menu = false
        },

        confirm() {
            // Nessuna modifica reale: chiudi soltanto, niente chiamata inutile
            if (!this.hasChanges) {
                this.close()
                return
            }

            this.saving = true
            const payload = {}
            payload[this.field] = this.multiple ? (this.draft || []) : this.draft

            this.axios.put(`helpdesktroubles-quickupdate/${this.ticketId}`, payload)
                .then(response => {
                    this.$store.commit('setSnackbar', {
                        value: true,
                        text: 'Ticket aggiornato',
                        color: 'success',
                        timeout: 3000
                    })
                    this.$emit('saved', response.data)
                    this.menu = false
                })
                .catch(error => {
                    // L'interceptor globale gestisce già la snackbar per 403/422/etc
                    console.error('Errore aggiornamento campo ticket:', error)
                })
                .finally(() => {
                    this.saving = false
                })
        }
    }
}
</script>

<style scoped>
.ticket-inline-edit__chip--empty {
    font-style: italic;
    opacity: 0.85;
}
</style>
